import { useState, KeyboardEvent, useEffect } from 'react';
import { X } from 'lucide-react';
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/lib/supabase";
import { captureError } from '@/lib/sentry';

interface ContactInterestsProps {
  contactId: string;
  onUpdate: () => void;
}

export function ContactInterests({ contactId, onUpdate }: ContactInterestsProps) {
  const [interests, setInterests] = useState<string[]>([]);
  const [newInterest, setNewInterest] = useState('');
  const { toast } = useToast();

  useEffect(() => {
    fetchInterests();
  }, [contactId]);

  const fetchInterests = async () => {
    try {
      const { data, error } = await supabase
        .from('contact_interests')
        .select('interest')
        .eq('contact_id', contactId);

      if (error) throw error;

      setInterests(data.map(item => item.interest));
    } catch (error) {
      console.error('Error fetching interests:', error);
    }
  };

  const addInterest = async (interest: string) => {
    const trimmedInterest = interest.trim().toLowerCase();
    if (!trimmedInterest || interests.includes(trimmedInterest)) return;

    try {
      const { error } = await supabase
        .from('contact_interests')
        .insert({
          contact_id: contactId,
          interest: trimmedInterest,
        });

      if (error) throw error;

      await fetchInterests();
      setNewInterest('');
      
      toast({
        description: "Interest added successfully",
      });
      onUpdate();
    } catch (error) {
      console.error('Error adding interest:', error);
      captureError(error as Error, { feature: 'contacts-contact-interests-add-interest' });
      toast({
        title: "Error",
        description: "Failed to add interest",
        variant: "destructive",
      });
    }
  };

  const removeInterest = async (interest: string) => {
    try {
      const { error } = await supabase
        .from('contact_interests')
        .delete()
        .eq('contact_id', contactId)
        .eq('interest', interest);

      if (error) throw error;

      await fetchInterests();
      
      toast({
        description: "Interest removed successfully",
      });
      onUpdate();
    } catch (error) {
      console.error('Error removing interest:', error);
      captureError(error as Error, { feature: 'contacts-contact-interests-remove-interest' });
      toast({
        title: "Error",
        description: "Failed to remove interest",
        variant: "destructive",
      });
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && newInterest.trim()) {
      e.preventDefault();
      addInterest(newInterest);
    }
  };

  return (
    <div>
      <Input
        value={newInterest}
        onChange={(e) => setNewInterest(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Type interest and press Enter"
        className="h-7 text-xs mb-2"
      />
      
      <div className="flex flex-wrap gap-1.5">
        {interests.map((interest) => (
          <Badge
            key={interest}
            variant="secondary"
            className="text-xs flex items-center gap-1"
          >
            {interest}
            <button
              onClick={() => removeInterest(interest)}
              className="ml-1 hover:bg-muted rounded-full"
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))}
      </div>
    </div>
  );
}